import { useCallback } from "react";

import { AccountViewer } from "components/form/graphql/types";
import useTypedContext from "hooks/useTypedContext";
import { Action, Stack } from "types/generated";
import { AccessResult, NO_ACCESS_MESSAGE, hasAtLeastOneRequiredAction } from "utils/access";
import { AccountContext } from "views/AccountWrapper";

import { checkTemplateDeploymentAccess } from "./helpers/checkTemplateDeploymentAccess";

const STACK_MANAGED_STATE_IMPORT_ACCESS_TYPES = [Action.SpaceAdmin, Action.StackManagedStateImport];

const STACK_HAS_UNFINISHED_RUNS_ERROR_MESSAGE =
  "Stack should have no unfinished runs to import a state";

const STACK_NOT_LOCKED_ERROR_MESSAGE = "Stack should be locked to import a state";

const hasStateImportAccess = (viewer: AccountViewer, spaceId: string) =>
  hasAtLeastOneRequiredAction(STACK_MANAGED_STATE_IMPORT_ACCESS_TYPES, viewer, spaceId);

type StackForStateImport = Pick<
  Stack,
  "spaceDetails" | "space" | "lockedBy" | "stateIsTerminal" | "isLockedByTemplateDeployment"
>;

/**
 * Hook that returns function to check if current user can import managed state into a stack.
 * User needs to be admin, or have SpaceAdmin or StackManagedStateImport action in the stack's space.
 * On top of that the stack must be locked by the current user and have no unfinished runs, and
 * stacks created from a template deployment can't have state imported.
 * @returns {(stack: StackForStateImport) => AccessResult} Function that takes a stack and returns an AccessResult indicating if user can import managed state and a message if not
 */
const useCanManagedStateImportStack = (): ((stack: StackForStateImport) => AccessResult) => {
  const { viewer } = useTypedContext(AccountContext);

  return useCallback(
    (stack) => {
      const templateDeploymentCheck = checkTemplateDeploymentAccess(stack);
      if (templateDeploymentCheck) {
        return templateDeploymentCheck;
      }

      if (!hasStateImportAccess(viewer, stack.spaceDetails?.id || stack.space)) {
        return { result: false, message: NO_ACCESS_MESSAGE };
      }

      if (!stack.stateIsTerminal) {
        return { result: false, message: STACK_HAS_UNFINISHED_RUNS_ERROR_MESSAGE };
      }

      if (!stack.lockedBy) {
        return { result: false, message: STACK_NOT_LOCKED_ERROR_MESSAGE };
      }

      if (stack.lockedBy !== viewer.id) {
        return {
          result: false,
          message: `Stack is locked by another user: ${stack.lockedBy}`,
        };
      }

      return { result: true };
    },
    [viewer]
  );
};

export default useCanManagedStateImportStack;
